import React, { useEffect, useState } from "react";

import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import BadgeIcon from "@mui/icons-material/Badge";
import BlurCircularIcon from "@mui/icons-material/BlurCircular";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import useCuasDrawing from "./useCuasDrawing";
import FlightIcon from "@mui/icons-material/Flight";
import ClearIcon from "@mui/icons-material/Clear";
import ModeOutlined from "@mui/icons-material/ModeOutlined";

import { Autocomplete, Collapse, Divider, IconButton, InputAdornment, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { useUserAuth } from "../contexts/authContext";
import { GetSource } from "../map/mapUtil";
import { ConvertFeetToMeters, ConvertMetersToFeet, getSourceIdsFromUserInput } from "../util";

export default function AssetCreator({ map, editFeature, setEditFeature, setAssetCreatorOpen, mobileDrawerOpen, setMobileDrawerOpen }) {
    const [drawType, setDrawType] = useState("Polygon");
    const [id, setId] = useState(0);
    const [uuid, setUuid] = useState(uuidv4());
    const [name, setName] = useState("");
    const [radiusFt, setRadiusFt] = useState(0);
    const [bufferFt, setBufferFt] = useState(0);
    const [bufferAltitudeAglFt, setBufferAltitudeAglFt] = useState(0);
    const [surveillanceFeeds, setSurveillanceFeeds] = useState([]);
    const [surveillanceFeed, setSurveillanceFeed] = useState("");

    const [nameError, setNameError] = useState("");
    const [radiusError, setRadiusError] = useState("");
    const [bufferError, setBufferError] = useState("");
    const [bufferAltitudeError, setBufferAltitudeError] = useState("");

    const [submitting, setSubmitting] = useState(false);
    const [oldEditFeature, setOldEditFeature] = useState(null);

    const assetCoords = useCuasDrawing(map, drawType, editFeature, radiusFt, setRadiusFt);
    const isDesktop = useMediaQuery("(min-width:900px)");

    const { user, handleFailedFetch } = useUserAuth();

    useEffect(() => {
        if (!editFeature?.getProperties()?.asset) {
            return;
        }

        // Add back the old edit feature if a new one is selected to be edited.
        if (oldEditFeature && oldEditFeature !== editFeature) {
            GetSource(map, "assets").addFeature(oldEditFeature);
        }

        const assetProperties = editFeature.get("asset");
        setId(assetProperties.id);
        setUuid(assetProperties.uuid);
        setName(assetProperties.name);
        setBufferFt(ConvertMetersToFeet(assetProperties.buffer_m));
        setBufferAltitudeAglFt(ConvertMetersToFeet(assetProperties.buffer_altitude_agl_m));
        setSurveillanceFeeds(assetProperties.surveillance_feeds || []);
        setOldEditFeature(editFeature);
        GetSource(map, "assets").removeFeature(editFeature);
    }, [editFeature]);

    useEffect(() => {
        if (!isDesktop && Array.isArray(assetCoords) && assetCoords.length > 0) {
            setMobileDrawerOpen(true);
        }
    }, [assetCoords]);

    const handleSubmitClick = () => {
        setNameError("");
        setBufferError("");
        setBufferAltitudeError("");

        if (!name) {
            return setNameError("Please enter an asset name.");
        }
        if (drawType === "Circle" && (isNaN(parseFloat(radiusFt)) || parseFloat(radiusFt) <= 0)) {
            return setRadiusError("Please enter a radius with a value above 0.");
        }
        if (isNaN(parseFloat(bufferFt)) || parseFloat(bufferFt) <= 0) {
            return setBufferError("Please enter a buffer with a value above 0.");
        }
        if (isNaN(parseFloat(bufferAltitudeAglFt)) || parseFloat(bufferAltitudeAglFt) <= 0) {
            return setBufferAltitudeError("Please enter an altitude buffer with a value above 0.");
        }
        if (assetCoords.length === 0) {
            return alert("Please create a valid asset volume to continue.");
        }

        setSubmitting(true);
        setEditFeature(null);

        const asset = {
            id,
            uuid,
            name,
            geometry: {
                type: "Polygon",
                coordinates: assetCoords
            },
            buffer_m: ConvertFeetToMeters(bufferFt),
            buffer_altitude_agl_m: ConvertFeetToMeters(bufferAltitudeAglFt),
            surveillance_feeds: surveillanceFeeds,
            active_monitoring: false,
            user_id: user.id,
            organization_id: user.organization_id
        };
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(asset)
        };
        fetch("/api/assets/insertupdate", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => setAssetCreatorOpen(false))
            .catch((err) => handleFailedFetch(err));
    };

    const handleDeleteClick = () => {
        if (!confirm("Are you sure you want to delete this asset?")) {
            return;
        }
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" }
        };
        fetch(`/api/assets/delete/${editFeature.get("asset").uuid}`, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => setAssetCreatorOpen(false))
            .catch((err) => handleFailedFetch(err));
    };

    const handleCancelClick = () => {
        setAssetCreatorOpen(false);
        if (editFeature) {
            GetSource(map, "assets").addFeature(editFeature);
        }
    };

    const handleDrawTypeChange = (e, value) => {
        if (value) {
            setDrawType(value);
        }
    };

    const handleFeedChange = (e, value) => {
        setSurveillanceFeed("");
        setSurveillanceFeeds(value);
    };

    const handleFeedKeyDown = (e) => {
        const value = getSourceIdsFromUserInput(e);
        if (value && (e.key === " " || e.key === "," || e.key === "Enter")) {
            setSurveillanceFeed("");
            setSurveillanceFeeds((prev) => [...prev, value]);
        } else {
            setSurveillanceFeed(value);
        }
    };

    const handleFeedBlur = (e) => {
        const value = getSourceIdsFromUserInput(e);
        if (value) {
            setSurveillanceFeed("");
            setSurveillanceFeeds((prev) => [...prev, value]);
        }
    };

    return (
        <>
            <Collapse in={isDesktop || mobileDrawerOpen} orientation="horizontal" sx={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
                <Box sx={{ p: 2, width: { xs: "100vw", md: "375px" }, height: "100%", overflowY: "auto" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="h6">{editFeature ? "Edit Asset" : "Create Asset"}</Typography>
                            {isDesktop ? (
                                <IconButton onClick={handleCancelClick}>
                                    <CloseIcon />
                                </IconButton>
                            ) : (
                                <Button onClick={() => setMobileDrawerOpen(false)} endIcon={<KeyboardArrowRightIcon />}>
                                    Back to Map
                                </Button>
                            )}
                        </Box>

                        <Divider />

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography variant="body1">Asset</Typography>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                                <BadgeIcon fontSize="small" />
                                <TextField
                                    fullWidth
                                    size="small"
                                    margin="dense"
                                    label="Asset Name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    error={nameError.length > 0}
                                    helperText={nameError}
                                    id="assetName"
                                />
                            </Box>
                            {drawType === "Circle" && (
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                                    <PlaceOutlinedIcon fontSize="small" />
                                    <TextField
                                        fullWidth
                                        size="small"
                                        margin="dense"
                                        label="Radius"
                                        value={radiusFt}
                                        onChange={(e) => setRadiusFt(e.target.value)}
                                        error={radiusError.length > 0}
                                        helperText={radiusError}
                                        InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                                        onWheel={(e) => e.target.blur()}
                                        id="assetRadius"
                                    />
                                </Box>
                            )}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                                <BlurCircularIcon fontSize="small" />
                                <TextField
                                    fullWidth
                                    size="small"
                                    margin="dense"
                                    label="Buffer"
                                    value={bufferFt}
                                    onChange={(e) => setBufferFt(e.target.value)}
                                    error={bufferError.length > 0}
                                    helperText={bufferError}
                                    InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                                    onWheel={(e) => e.target.blur()}
                                    type="number"
                                    id="assetBuffer"
                                />
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                                <ArrowUpwardIcon fontSize="small" />
                                <TextField
                                    fullWidth
                                    size="small"
                                    margin="dense"
                                    label="Altitude Buffer (AGL)"
                                    value={bufferAltitudeAglFt}
                                    onChange={(e) => setBufferAltitudeAglFt(e.target.value)}
                                    error={bufferAltitudeError.length > 0}
                                    helperText={bufferAltitudeError}
                                    InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                                    onWheel={(e) => e.target.blur()}
                                    type="number"
                                    id="assetBufferAltitude"
                                />
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                                <FlightIcon fontSize="small" />
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={[]}
                                    value={surveillanceFeeds}
                                    inputValue={surveillanceFeed}
                                    onChange={handleFeedChange}
                                    onInputChange={(e) => setSurveillanceFeed(getSourceIdsFromUserInput(e))}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Surveillance Feeds (Source IDs)"
                                            value={surveillanceFeed}
                                            onKeyDown={handleFeedKeyDown}
                                            onBlur={handleFeedBlur}
                                            helperText="If empty, this asset will alert on all sources."
                                            id="assetFeeds"
                                        />
                                    )}
                                    fullWidth
                                    size="small"
                                    margin="dense"
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Button fullWidth variant="contained" loading={submitting} onClick={handleSubmitClick} id="assetSubmit">
                                Submit
                            </Button>
                            {editFeature && (
                                <Button fullWidth variant="outlined" color="error" onClick={handleDeleteClick} id="assetDelete">
                                    Delete
                                </Button>
                            )}
                            <Button fullWidth variant="text" onClick={handleCancelClick} id="assetCancel">
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Collapse>
            {!mobileDrawerOpen && (
                <Box sx={{ position: "relative" }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            position: "absolute",
                            left: "15px",
                            top: "15px",
                            zIndex: 100,
                            background: "#121212"
                        }}
                    >
                        <ToggleButtonGroup
                            orientation="vertical"
                            color="primary"
                            value={drawType}
                            onChange={handleDrawTypeChange}
                            exclusive
                            sx={{ width: "48px" }}
                        >
                            {[
                                {
                                    name: "Polygon",
                                    icon: <ModeOutlined sx={{ width: "100%" }} />
                                },
                                {
                                    name: "Circle",
                                    icon: <PlaceOutlinedIcon sx={{ width: "100%" }} />
                                },
                                {
                                    name: "Clear",
                                    icon: <ClearIcon sx={{ width: "100%" }} />
                                }
                            ].map((drawType, i) => (
                                <Tooltip key={i} title={drawType.name} placement="right">
                                    <ToggleButton value={drawType.name} id={drawType.name.toLowerCase()}>
                                        {drawType.icon}
                                    </ToggleButton>
                                </Tooltip>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                </Box>
            )}
        </>
    );
}
